import isEqual from 'fast-deep-equal';
import { ElementType, memo, useRef, useState } from 'react';
import { Snackbar, Box, styled } from '@/theme/index';
import { ReportSectionData } from '@/utils/helperTypes';
import { ThumbRating } from './ThumbRating';
import { RemoveDataButton } from './feedbackButtons';
import { RemoveDataModal, UndoModal } from './feedbackModals';
import { useRemoveData } from './feedbackUtils';
import { NormalizedBonusData } from '../sections/FreeBonusData/LicensesAndPermits/types';
import { serializedTypes } from '../types';

interface ThumbRatingContainerProps {
  component: ElementType;
  item: ReportSectionData | serializedTypes.Connection | NormalizedBonusData;
  dataPropKey: string;
  permalink: string;
  cardProps?: Record<string, unknown>;
  showCityAndStateAddress?: boolean;
  usesFamilyTreeCta?: boolean;
  addedToFamilyTree?: boolean;
  addRelativeToFamilyTree?: () => void;
  hasFamilyTreeCreated?: boolean;
}

export const ThumbRatingContainer = memo(
  ({
    component: Component,
    item,
    dataPropKey,
    permalink,
    cardProps,
    showCityAndStateAddress = false,
    usesFamilyTreeCta = false,
    addedToFamilyTree = false,
    hasFamilyTreeCreated,
    addRelativeToFamilyTree,
  }: ThumbRatingContainerProps) => {
    const data = { [dataPropKey]: item };
    const uniqueIdenitifier = item.jsonPath as string;
    const containerRef = useRef(null);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [undoModalOpen, setUndoModalOpen] = useState(false);
    const [open, setSnackbarOpen] = useState('');

    const StyledMemoizedSnackbar = memo(
      styled(Snackbar)(({ theme }) => ({
        position: 'absolute',
        [theme.breakpoints.up('sm')]: {
          left: theme.spacing(15.5),
          right: 'unset',
          bottom: item.downvoted ? theme.spacing(5) : theme.spacing(0.5),
        },
      })),
      (prevProps, nextProps) => prevProps.open === nextProps.open
    );

    const handleRemoveModalOpen = (change: boolean) =>
      setTimeout(() => setRemoveModalOpen(change), 0);
    const handleUndoModalOpen = (change: boolean) =>
      setTimeout(() => setUndoModalOpen(change), 0);
    const handleSnackBarOpen = () => {
      setSnackbarOpen(uniqueIdenitifier);
    };

    const removeMutation = useRemoveData(permalink, () =>
      handleUndoModalOpen(false)
    );

    const handleTimeout = () => {
      setTimeoutId(
        setTimeout(
          () =>
            removeMutation.mutate({
              jsonPath: item.jsonPath as string,
              permalink,
            }),
          3000
        )
      );
    };
    const handleRemoveAction = () => {
      handleRemoveModalOpen(false);
      handleUndoModalOpen(true);
      handleTimeout();
    };

    const handleUndoAction = () => {
      clearTimeout(timeoutId);
      handleUndoModalOpen(false);
    };

    if (item.hidden) return null;
    return (
      <Box
        overflow="visible !important"
        position="relative"
        marginBottom={2.5}
        ref={containerRef}
      >
        <Component
          {...data}
          {...cardProps}
          usesFamilyTreeCta={usesFamilyTreeCta}
          addedToFamilyTree={addedToFamilyTree}
          hasFamilyTreeCreated={hasFamilyTreeCreated}
          addRelativeToFamilyTree={addRelativeToFamilyTree}
          permalink={permalink}
          thumbRating={() => (
            <ThumbRating
              item={item}
              permalink={permalink}
              setSnackbarOpen={handleSnackBarOpen}
            />
          )}
          showCityAndStateAddress={showCityAndStateAddress}
        />
        <RemoveDataButton
          item={item}
          handleClick={() => handleRemoveModalOpen(true)}
        />
        <StyledMemoizedSnackbar
          open={open === uniqueIdenitifier}
          onClose={() => setSnackbarOpen('')}
          autoHideDuration={3500}
          message="Thanks for helping us improve our data"
          ContentProps={{
            sx: {
              color: 'black',
              backgroundColor: 'white',
              maxWidth: '175px',
              boxShadow: '0px 2px 4px 0px #53535380',
            },
          }}
        />
        <RemoveDataModal
          containerRef={containerRef}
          open={removeModalOpen}
          handleOpen={handleRemoveModalOpen}
          handleAction={handleRemoveAction}
        />
        <UndoModal
          containerRef={containerRef}
          open={undoModalOpen}
          handleOpen={handleUndoModalOpen}
          handleAction={handleUndoAction}
        />
      </Box>
    );
  },
  (prevProps, nextProps) => {
    const prevItem = prevProps.item;
    const nextItem = nextProps.item;
    const isEqualItem = isEqual(prevItem, nextItem);
    const isFamilyTreeEqual =
      prevProps.hasFamilyTreeCreated === nextProps.hasFamilyTreeCreated;

    return isEqualItem && isFamilyTreeEqual;
  }
);
